/**
 * Taalbegrip (TAA) Support Registry
 *
 * Support content (hints, explanations, visuals) for TAA families.
 *
 * Module: TAA (Taalbegrip)
 * Families: 35
 * Coverage: 100% (migrated: 2026-03-25)
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry';

/**
 * TAA Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const TAA_SUPPORT: Record<string, FamilySupportEntry> = {
  'TAA-ONT-BASIS': {
    familyId: 'TAA-ONT-BASIS',
    defaultExplanation: 'De persoonsvorm vind je door de tijd te veranderen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Persoonsvorm vinden',
      content: 'Verander de tijd van de zin. Het woord dat verandert is de persoonsvorm.\n\n"Sophie rent naar school" → "Sophie rende naar school"\nrent → rende, dus "rent" is de persoonsvorm.\n\nOf verander de persoon:\n"Ik loop" → "Wij lopen" → loop/lopen verandert = persoonsvorm.'
    }, {
      title: 'Onderwerp vinden',
      content: 'Stel de vraag: "Wie of wat + persoonsvorm?"\n\nVoorbeeld: "De hond blaft luid."\nPersoonsvorm = blaft\nWie of wat blaft? → De hond = onderwerp\n\nLet op: het onderwerp is de hele woordgroep, inclusief het lidwoord!'
    }, {
      title: 'Pas op bij inversie!',
      content: 'Het onderwerp staat niet altijd vooraan in de zin.\n\n"Morgen gaat Lisa naar school."\nWie of wat gaat? → Lisa (niet "Morgen"!)\n\n"Op het plein spelen kinderen."\nWie of wat spelen? → kinderen (niet "het plein"!)\n\nGebruik altijd de wie/wat-vraag, niet de woordvolgorde.'
    }]
  },
  'TAA-SOORT-LID': {
    familyId: 'TAA-SOORT-LID',
    defaultExplanation: 'Lidwoorden: de, het, een.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Overzicht',
      content: 'Zelfstandig naamwoord: mens, dier, ding (de/het ervoor)\nBijvoeglijk naamwoord: hoe is iets? (groot, rood)\nWerkwoord: wat doe je? (lopen, zijn)\nLidwoord: de, het, een'
    }]
  },
  'TAA-LEZEN-VERW': {
    familyId: 'TAA-LEZEN-VERW',
    defaultExplanation: 'Een verwijswoord verwijst naar iets in een eerdere zin.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Verwijswoord herkennen',
      content: 'Een verwijswoord vervangt een woord of zinsdeel dat eerder in de tekst staat.\n\nVoorbeelden: hij, zij, het, die, dat, daar, dit, ze, hem, haar\n\nLees altijd de zin ervoor om te vinden waar het naar verwijst.'
    }, {
      title: 'Soorten verwijswoorden',
      content: 'Personen: hij, zij, ze, hem, haar → verwijzen naar personen/dieren\nPlaats: daar, hier, er → verwijzen naar een plek\nZaken: het, dit, dat → verwijzen naar een ding of handeling\n\n"De kat sprong op de tafel. Daar lag een vis."\ndaar = plaatsverwijswoord → de tafel'
    }, {
      title: 'Strategie: twee stappen',
      content: 'Stap 1: Bepaal het soort verwijswoord (persoon, plaats of zaak)\nStap 2: Zoek in de vorige zin(nen) naar het woord van dat soort\n\nVoorbeeld: "De hond rende naar de bal. Hij pakte hem op."\nhij = persoon → de hond (onderwerp)\nhem = zaak → de bal (lijdend voorwerp)\n\nPas op: "dat" kan naar een hele handeling verwijzen!'
    }]
  },
  'TAA-ONT-LV-MV': {
    familyId: 'TAA-ONT-LV-MV',
    defaultExplanation: 'Lijdend voorwerp: "Wie/wat + pv + onderwerp?"',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Hoe vind je het?',
      content: 'Vraag: "Wie/wat + persoonsvorm + onderwerp?"\n\nVoorbeeld: De kok bakt een pannenkoek.\nWat bakt de kok? → een pannenkoek = lijdend voorwerp'
    }]
  },
  'TAA-SOORT-VNW': {
    familyId: 'TAA-SOORT-VNW',
    defaultExplanation: 'Voornaamwoorden vervangen een zelfstandig naamwoord.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Overzicht',
      content: 'ik, jij, hij, zij, wij, jullie, zij, het, dit, dat, wie, wat, mijn, jouw'
    }]
  },
  'TAA-LEZEN-KERN': {
    familyId: 'TAA-LEZEN-KERN',
    defaultExplanation: 'De hoofdgedachte is het belangrijkste punt van de tekst.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Strategie: rode draad',
      content: 'Vraag jezelf: "Als ik de tekst in één zin moet samenvatten, wat zou ik zeggen?"\nDe hoofdgedachte dekt de hele tekst, niet één detail.'
    }, {
      title: 'Afleiders herkennen',
      content: 'Pas op voor:\n• Details die maar in één zin staan → dat is niet de hoofdgedachte\n• Informatie die NIET in de tekst staat → eigen kennis is geen hoofdgedachte\n• Overdrijvingen ("alle", "altijd", "nooit") → check of de tekst dat echt zegt'
    }, {
      title: 'Bij voor- en nadelen',
      content: 'Noemt de tekst zowel voordelen als nadelen?\nDan is de hoofdgedachte vaak een afweging — niet alleen positief of alleen negatief.\nZoek de conclusie van de schrijver.'
    }]
  },
  'TAA-ONT-BWB': {
    familyId: 'TAA-ONT-BWB',
    defaultExplanation: 'Bijwoordelijke bepalingen geven extra informatie over plaats, tijd, wijze of reden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Hoe vind je het?',
      content: 'Vraag: "Waar?", "Wanneer?", "Hoe?", "Waarom?"'
    }]
  },
  'TAA-LEZEN-FEIT': {
    familyId: 'TAA-LEZEN-FEIT',
    defaultExplanation: 'Een feit is controleerbaar. Een mening is wat iemand vindt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Feit',
      content: 'Controleerbaar en meetbaar.\nSignalen: cijfers, onderzoek'
    }, {
      title: 'Mening',
      content: 'Wat iemand vindt.\nSignalen: "ik vind", "zou moeten", "volgens mij"'
    }]
  },
  'TAA-LEZEN-VERB': {
    familyId: 'TAA-LEZEN-VERB',
    defaultExplanation: 'Signaalwoorden geven het verband aan.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Tegenstelling',
      content: 'maar, toch, hoewel, desondanks, echter'
    }, {
      title: 'Oorzaak-gevolg',
      content: 'daarom, dus, hierdoor, doordat, daardoor, omdat, want'
    }]
  },
  'TAA-ONT-GEZEGDE': {
    familyId: 'TAA-ONT-GEZEGDE',
    defaultExplanation: 'Het gezegde is alles wat je over het onderwerp zegt. Het bevat altijd de persoonsvorm en soms nog meer werkwoorden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Gezegde vinden',
      content: 'Het gezegde = alle werkwoorden in de zin.\n\n"Jan heeft een boek gelezen."\nGezegde: heeft gelezen\nPersoonsvorm: heeft'
    }, {
      title: 'Werkwoordelijk vs naamwoordelijk',
      content: 'Werkwoordelijk gezegde: alleen werkwoorden\n"Hij loopt naar school."\n\nNaamwoordelijk gezegde: koppelww + bijvoeglijk nw\n"Hij is ziek." (is = koppelww, ziek = naamwoordelijk deel)'
    }]
  },
  'TAA-ONT-NAAMWOORDELIJK': {
    familyId: 'TAA-ONT-NAAMWOORDELIJK',
    defaultExplanation: 'Het naamwoordelijk deel van het gezegde staat bij een koppelwerkwoord (zijn, worden, blijven, lijken, schijnen, heten).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Koppelwerkwoorden',
      content: 'zijn, worden, blijven, lijken, schijnen, heten, dunken\n\n"De kat is zwart." → zwart = naamwoordelijk deel\n"Hij wordt arts." → arts = naamwoordelijk deel'
    }]
  },
  'TAA-ONT-VOORZETSEL': {
    familyId: 'TAA-ONT-VOORZETSEL',
    defaultExplanation: 'Een voorzetselvoorwerp begint met een vast voorzetsel dat bij het werkwoord hoort: wachten op, denken aan, luisteren naar.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Herkennen',
      content: 'Het voorzetsel hoort VAST bij het werkwoord:\nwachten OP → "Ik wacht op de bus."\ndenken AAN → "Ik denk aan jou."\nluisteren NAAR → "Ik luister naar muziek."'
    }, {
      title: 'Verschil met BWB',
      content: 'Voorzetselvoorwerp: vast voorzetsel\n"Ik denk AAN jou." (denken aan = vast)\n\nBijwoordelijke bepaling: los voorzetsel\n"Ik zit IN de tuin." (zitten in = niet vast)'
    }]
  },
  'TAA-ONT-BIJZIN': {
    familyId: 'TAA-ONT-BIJZIN',
    defaultExplanation: 'Een bijzin kan niet alleen staan en begint vaak met een voegwoord (dat, omdat, als, wanneer, die, dat).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Herkennen',
      content: 'Bijzin begint met een voegwoord:\ndat, omdat, als, wanneer, hoewel, terwijl\ndie, dat, wie, wat (betrekkelijk)\n\nDe persoonsvorm staat ACHTERAAN in een bijzin.'
    }]
  },
  'TAA-ONT-HOOFDZIN': {
    familyId: 'TAA-ONT-HOOFDZIN',
    defaultExplanation: 'Een hoofdzin kan alleen staan en heeft de persoonsvorm op de tweede plaats.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Hoofdzin herkennen',
      content: 'De persoonsvorm staat op de 2e plaats:\n"Jan loopt naar school."\n1=Jan, 2=loopt\n\nBij een vraag op de 1e plaats:\n"Loopt Jan naar school?"'
    }]
  },
  'TAA-ONT-BETREKKELIJK': {
    familyId: 'TAA-ONT-BETREKKELIJK',
    defaultExplanation: 'Een betrekkelijke bijzin geeft extra informatie over een zelfstandig naamwoord. Het begint met die, dat, wie of wat.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Die of dat?',
      content: 'De-woorden → die: "De man die daar loopt..."\nHet-woorden → dat: "Het boek dat ik lees..."\n\nBij personen altijd "die":\n"Het meisje dat/die daar staat..." → die'
    }]
  },
  'TAA-ONT-INVERSIE': {
    familyId: 'TAA-ONT-INVERSIE',
    defaultExplanation: 'Bij inversie staat het onderwerp ACHTER de persoonsvorm. Dit gebeurt als de zin niet met het onderwerp begint.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Inversie herkennen',
      content: 'Normaal: "Jan loopt naar school." (onderwerp eerst)\nInversie: "Naar school loopt Jan." (onderwerp na pv)\n\nDe persoonsvorm blijft ALTIJD op plek 2!'
    }]
  },
  'TAA-SOORT-BNW': {
    familyId: 'TAA-SOORT-BNW',
    defaultExplanation: 'Een bijvoeglijk naamwoord zegt iets over een zelfstandig naamwoord: de GROTE hond, het MOOIE huis.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Herkennen',
      content: 'Vraag: "Hoe is het?" of "Welk soort?"\nDe grote hond → Hoe is de hond? → groot\nHet oude huis → Hoe is het huis? → oud'
    }, {
      title: 'Trappen van vergelijking',
      content: 'Stellend: groot\nVergrotend: groter\nOvertreffend: grootst\n\nOnregelmatig: goed → beter → best'
    }]
  },
  'TAA-SOORT-WW': {
    familyId: 'TAA-SOORT-WW',
    defaultExplanation: 'Een werkwoord drukt een handeling of toestand uit: lopen, zijn, denken. Het hele werkwoord eindigt op -en.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Herkennen',
      content: 'Vraag: "Wat doet/is het onderwerp?"\nJan loopt → Wat doet Jan? → lopen\nDe kat slaapt → Wat doet de kat? → slapen'
    }]
  },
  'TAA-SOORT-BW': {
    familyId: 'TAA-SOORT-BW',
    defaultExplanation: 'Een bijwoord zegt iets over een werkwoord, bijvoeglijk naamwoord of ander bijwoord: snel lopen, heel mooi, erg snel.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Herkennen',
      content: 'Bijwoord bij werkwoord: "Hij loopt SNEL."\nBijwoord bij bnw: "Het is HEEL mooi."\nBijwoord bij bijwoord: "Hij loopt ERG snel."'
    }]
  },
  'TAA-SOORT-VZ': {
    familyId: 'TAA-SOORT-VZ',
    defaultExplanation: 'Een voorzetsel geeft een relatie aan tussen woorden: in, op, aan, met, voor, door, naar, van, uit, bij, over, onder, tussen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Veelgebruikte voorzetsels',
      content: 'Plaats: in, op, aan, bij, onder, boven, tussen, naast\nRichting: naar, uit, door, over\nTijd: om, na, voor, tijdens, sinds\nRelatie: met, voor, van, door'
    }]
  },
  'TAA-SOORT-VW': {
    familyId: 'TAA-SOORT-VW',
    defaultExplanation: 'Een voegwoord verbindt woorden, zinsdelen of zinnen: en, maar, of, want, omdat, dat, als, hoewel.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Nevenschikkend',
      content: 'Verbinden gelijkwaardige delen:\nen, maar, of, want, dus, noch\n\n"Jan EN Piet lopen."\n"Het regent, MAAR ik ga toch."'
    }, {
      title: 'Onderschikkend',
      content: 'Beginnen een bijzin:\ndat, omdat, als, wanneer, hoewel, terwijl, zodat\n\n"Ik blijf thuis OMDAT het regent."'
    }]
  },
  'TAA-SOORT-TW': {
    familyId: 'TAA-SOORT-TW',
    defaultExplanation: 'Een telwoord geeft een aantal of volgorde aan: drie, honderd (hoofdtelwoord) of derde, honderdste (rangtelwoord).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Twee soorten',
      content: 'Hoofdtelwoorden (hoeveel?): een, twee, drie, honderd\nRangtelwoorden (de hoeveelste?): eerste, tweede, derde\n\n"Er zijn DRIE appels." (hoofdtelwoord)\n"Dit is de DERDE keer." (rangtelwoord)'
    }]
  },
  'TAA-SIGNAALWOORD': {
    familyId: 'TAA-SIGNAALWOORD',
    defaultExplanation: 'Signaalwoorden geven het verband tussen zinnen aan: oorzaak-gevolg (daarom), tegenstelling (maar), opsomming (ook).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Soorten verbanden',
      content: 'Opsomming: ook, bovendien, ten eerste\nTegenstelling: maar, toch, echter, hoewel\nOorzaak-gevolg: daarom, dus, doordat, omdat\nTijd: daarna, vervolgens, ten slotte\nVoorwaarde: als, mits, tenzij'
    }]
  },
  'TAA-TEKSTSOORT': {
    familyId: 'TAA-TEKSTSOORT',
    defaultExplanation: 'Verschillende tekstsoorten hebben verschillende doelen: informeren, overtuigen, amuseren, instrueren.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Tekstsoorten',
      content: 'Informatief: krant, encyclopedie (feiten)\nBetogend: ingezonden brief (mening)\nInstructief: recept, handleiding (stappen)\nVerhalend: roman, sprookje (verhaal)\nBeschouwend: essay (afwegen)'
    }]
  },
  'TAA-LEZEN-DETAIL': {
    familyId: 'TAA-LEZEN-DETAIL',
    defaultExplanation: 'Bij detailvragen zoek je specifieke informatie in de tekst. Lees de vraag goed en scan de tekst op sleutelwoorden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Strategie',
      content: 'Stap 1: Lees de vraag (wat zoek je?)\nStap 2: Zoek sleutelwoorden in de vraag\nStap 3: Scan de tekst op die woorden\nStap 4: Lees de zin(nen) eromheen'
    }]
  },
  'TAA-LEZEN-CONCLUSIE': {
    familyId: 'TAA-LEZEN-CONCLUSIE',
    defaultExplanation: 'Bij conclusievragen combineer je informatie uit de tekst om iets af te leiden dat er niet letterlijk staat.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Strategie',
      content: 'Een conclusie staat NIET letterlijk in de tekst.\nJe leidt het af uit wat er WEL staat.\n\nVraag jezelf: "Wat kun je hieruit opmaken?"\nControleer: past mijn conclusie bij ALLE informatie?'
    }]
  },
  'TAA-LEZEN-VOORSPEL': {
    familyId: 'TAA-LEZEN-VOORSPEL',
    defaultExplanation: 'Bij voorspelvragen gebruik je aanwijzingen in de tekst om te bedenken wat er daarna zou kunnen gebeuren.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Strategie',
      content: 'Kijk naar:\n• De titel en tussenkoppen\n• Plaatjes en bijschriften\n• De eerste en laatste alinea\n• Signaalwoorden\n\nVraag: "Wat zou er logisch volgen?"'
    }]
  },
  'TAA-LEZEN-SAMENVATTEN': {
    familyId: 'TAA-LEZEN-SAMENVATTEN',
    defaultExplanation: 'Bij samenvatten zoek je de kern van elke alinea en combineer je die tot een kort overzicht.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Lees de tekst helemaal\nStap 2: Zoek per alinea de kernzin\nStap 3: Combineer de kernzinnen\nStap 4: Schrijf in eigen woorden\nStap 5: Laat details en voorbeelden weg'
    }]
  },
  'TAA-LEZEN-STRUCTUUR': {
    familyId: 'TAA-LEZEN-STRUCTUUR',
    defaultExplanation: 'Tekststructuur herkennen: inleiding, kern, slot. Let op signaalwoorden die de opbouw aangeven.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Structuur',
      content: 'Inleiding: introductie van het onderwerp\nKern: uitwerking, argumenten, voorbeelden\nSlot: conclusie, samenvatting\n\nSignaalwoorden helpen:\n"Ten eerste..." "Daarnaast..." "Tot slot..."'
    }]
  },
  'TAA-LEZEN-DOEL': {
    familyId: 'TAA-LEZEN-DOEL',
    defaultExplanation: 'Het doel van de auteur bepalen: wil de schrijver informeren, overtuigen, vermaken of instrueren?',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Doelen herkennen',
      content: 'Informeren: feiten, cijfers, neutraal\nOvertuigen: mening, argumenten, "je moet..."\nVermaken: verhaal, humor, spanning\nInstrueren: stappen, "doe eerst..., dan..."'
    }]
  },
  'TAA-ARGUMENTATIE': {
    familyId: 'TAA-ARGUMENTATIE',
    defaultExplanation: 'Bij argumentatie onderscheid je het standpunt (wat vindt de schrijver?) van de argumenten (waarom vindt hij dat?).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Standpunt vs argument',
      content: 'Standpunt: de mening van de schrijver\n"Huisdieren zijn goed voor kinderen."\n\nArgument: de reden waarom\n"Want kinderen leren verantwoordelijkheid."\n\nSignaalwoorden: want, omdat, namelijk, immers'
    }]
  },
  'TAA-WOORD-SYNONIEM': {
    familyId: 'TAA-WOORD-SYNONIEM',
    defaultExplanation: 'Synoniemen zijn woorden met (bijna) dezelfde betekenis: groot = fors, mooi = fraai, lopen = wandelen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Strategie',
      content: 'Vervang het woord door het synoniem.\nKlinkt de zin nog hetzelfde?\n\n"De grote boom" → "De forse boom" ✓\n"De grote boom" → "De hoge boom" ✓ (maar iets anders)'
    }]
  },
  'TAA-WOORD-ANTONIEM': {
    familyId: 'TAA-WOORD-ANTONIEM',
    defaultExplanation: 'Antoniemen zijn woorden met een tegengestelde betekenis: groot ↔ klein, mooi ↔ lelijk, snel ↔ langzaam.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Herkennen',
      content: 'Het tegenovergestelde:\ngroot ↔ klein\nwarm ↔ koud\nsnel ↔ langzaam\nvrolijk ↔ verdrietig\nbegin ↔ einde'
    }]
  },
  'TAA-WOORD-CONTEXT': {
    familyId: 'TAA-WOORD-CONTEXT',
    defaultExplanation: 'De betekenis van een onbekend woord kun je vaak afleiden uit de context: de zinnen eromheen geven aanwijzingen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Strategie',
      content: 'Stap 1: Lees de hele zin\nStap 2: Lees de zinnen ervoor en erna\nStap 3: Welk woord zou hier passen?\nStap 4: Controleer: klopt het met de rest van de tekst?'
    }]
  },
  'TAA-WOORD-IDIOOM': {
    familyId: 'TAA-WOORD-IDIOOM',
    defaultExplanation: 'Idioom en uitdrukkingen hebben een figuurlijke betekenis: "de spijker op de kop slaan" = precies goed zeggen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Veelgebruikte uitdrukkingen',
      content: 'De spijker op de kop slaan = precies goed\nMet de deur in huis vallen = direct ter zake\nDoor de zure appel bijten = iets vervelends doen\nIemand een hart onder de riem steken = aanmoedigen'
    }]
  }
};

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const TAA_SUPPORT_STATS = {
  moduleCode: 'TAA',
  moduleName: 'Taalbegrip',
  totalFamilies: Object.keys(TAA_SUPPORT).length,
  withHints: Object.values(TAA_SUPPORT).filter(e => e.hints?.length).length,
  withVisuals: Object.values(TAA_SUPPORT).filter(e => e.defaultVisual).length,
  withExplanations: Object.values(TAA_SUPPORT).filter(e => e.defaultExplanation).length,
  requiresHintsCount: Object.values(TAA_SUPPORT).filter(e => e.requiresHints).length,
  requiresVisualCount: Object.values(TAA_SUPPORT).filter(e => e.requiresVisual).length
};